import { Ban, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Alert, AlertDescription, Button, ConfirmDialog, FormField, Textarea } from '@payment-plan/ui';
import type { AnulacionSeparacion, DescarteSeparacion } from '../../../types/consultas';
import { mensajeDeError } from '../../separations/errors';
import { MOTIVO_ANULACION_MAX, MOTIVO_ANULACION_MIN } from '../estados';
import { useAnularSeparacion, useDescartarSeparacion } from '../use-cierre-separacion';

/**
 * feat-67 — acciones de cierre de una separación con el `ConfirmDialog` común (AGENTS §9):
 * descartar un borrador y anular una separación emitida (con motivo). El back decide permisos,
 * alcance y la matriz de transiciones; aquí solo se ofrece la acción donde tiene sentido.
 */

export const MENSAJE_DESCARTADA = 'Borrador descartado. La unidad quedó disponible.';
export const MENSAJE_ANULADA = 'Separación anulada. La unidad quedó disponible.';

interface DescartarBorradorProps {
  separacionId: string;
  /** Texto accesible del botón (p. ej. con la unidad, en una tabla). */
  etiqueta?: string;
  onDescartada?: (resultado: DescarteSeparacion) => void;
  onError?: (mensaje: string) => void;
}

export function DescartarBorradorButton({
  separacionId,
  etiqueta,
  onDescartada,
  onError,
}: DescartarBorradorProps): React.JSX.Element {
  const [abierto, setAbierto] = React.useState(false);
  const descartar = useDescartarSeparacion();

  return (
    <>
      <Button variant="outline" size="lg" className="px-4" aria-label={etiqueta} onClick={() => setAbierto(true)}>
        <Trash2 className="h-4 w-4" aria-hidden="true" />
        Descartar borrador
      </Button>
      <ConfirmDialog
        open={abierto}
        onOpenChange={setAbierto}
        title="Descartar borrador"
        description="La separación pasará a «Descartada» y la unidad vuelve a quedar disponible para separar. Esta acción no se puede deshacer."
        confirmText="Descartar borrador"
        variant="destructive"
        loading={descartar.isPending}
        onConfirm={() => {
          // `mutateAsync` (no los callbacks de `mutate`): la fila que lanzó la acción se desmonta cuando la
          // consulta se refresca con el nuevo estado, y el aviso lo muestra la pantalla.
          void descartar.mutateAsync(separacionId).then(
            (r) => onDescartada?.(r),
            (e: unknown) => onError?.(mensajeDeError(e, 'descartar el borrador')),
          );
        }}
      />
    </>
  );
}

interface AnularSeparacionProps {
  separacionId: string;
  /** Inmobiliaria destino (consulta administrativa, ADR-018). */
  inmobiliariaId?: string;
  onAnulada?: (resultado: AnulacionSeparacion) => void;
  onError?: (mensaje: string) => void;
}

function motivoValido(motivo: string): boolean {
  const largo = motivo.trim().length;
  return largo >= MOTIVO_ANULACION_MIN && largo <= MOTIVO_ANULACION_MAX;
}

export function AnularSeparacionButton({
  separacionId,
  inmobiliariaId,
  onAnulada,
  onError,
}: AnularSeparacionProps): React.JSX.Element {
  const [abierto, setAbierto] = React.useState(false);
  const [motivo, setMotivo] = React.useState('');
  const anular = useAnularSeparacion();
  const campoId = React.useId();
  const valido = motivoValido(motivo);
  const error = motivo.length > 0 && !valido ? `Escribe entre ${MOTIVO_ANULACION_MIN} y ${MOTIVO_ANULACION_MAX} caracteres.` : undefined;

  return (
    <>
      <Button variant="destructive" size="lg" className="px-4" onClick={() => setAbierto(true)}>
        <Ban className="h-4 w-4" aria-hidden="true" />
        Anular separación
      </Button>
      <ConfirmDialog
        open={abierto}
        onOpenChange={(open) => {
          setAbierto(open);
          if (!open) setMotivo('');
        }}
        title="Anular separación"
        description="La separación pasará a «Anulada» y sus unidades quedarán disponibles. Los documentos emitidos se conservan en el historial y no se modifican. Esta acción no se puede deshacer."
        confirmText="Anular separación"
        variant="destructive"
        loading={anular.isPending}
        confirmDisabled={!valido}
        onConfirm={() => {
          const recortado = motivo.trim();
          void anular.mutateAsync({ separacionId, motivo: recortado, inmobiliariaId }).then(
            (r) => onAnulada?.(r),
            (e: unknown) => onError?.(mensajeDeError(e, 'anular la separación')),
          );
        }}
      >
        <FormField label="Motivo de la anulación" htmlFor={campoId} required error={error} description={`${motivo.trim().length}/${MOTIVO_ANULACION_MAX} caracteres`}>
          <Textarea
            id={campoId}
            value={motivo}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMotivo(e.target.value)}
            placeholder="Ej.: el separante desiste de la compra"
            error={Boolean(error)}
          />
        </FormField>
      </ConfirmDialog>
    </>
  );
}

/** Resultado del cierre: éxito (`status`) o error de negocio (`alert`), para anunciarlo a lectores de pantalla. */
export function CierreFeedback({ exito, error }: { exito: string | null; error: string | null }): React.JSX.Element {
  return (
    <>
      {error && (
        <Alert variant="destructive" role="alert" aria-label="Error del cierre">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <p role="status" aria-label="Resultado del cierre" aria-live="polite" className="text-sm font-medium text-success empty:hidden">
        {exito ?? ''}
      </p>
    </>
  );
}
